import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { Route } from "./+types/price-list";
import { Container } from "~/components/layout/Container";
import {
  getDefaultPricesByItemIds,
  getMerchantById,
  getMerchantPublicOffers,
  getOfferSpecificPrices,
} from "~/lib/api/merchants.server";
import { getServiceItemsByIds } from "~/lib/api/services.server";
import { safe } from "~/lib/safe";
import { cacheHeaders } from "~/lib/cache-headers";
import type { ResolvedPrice } from "~/lib/types";

type PriceListEntry = {
  offerId: string;
  itemId: string;
  itemName: string;
  itemDescription: string | null;
  price: ResolvedPrice | null;
};

export async function loader({ params }: Route.LoaderArgs) {
  const { id } = params;
  const merchant = await getMerchantById(id);
  if (!merchant) throw new Response("Not Found", { status: 404 });

  const offers = await safe(() => getMerchantPublicOffers(id), []);

  if (offers.length === 0) {
    return { merchant, priceList: [] as PriceListEntry[] };
  }

  const itemIds = [...new Set(offers.map(o => o.item_id))];
  const offerIds = offers.map(o => o.id);

  const [items, offerPrices, defaultPrices] = await Promise.all([
    safe(() => getServiceItemsByIds(itemIds), []),
    safe(() => getOfferSpecificPrices(offerIds), []),
    safe(() => getDefaultPricesByItemIds(itemIds), []),
  ]);

  const itemMap = new Map(items.map(i => [i.id, i]));
  const offerPriceByOfferId = new Map(
    offerPrices.map(p => [p.merchant_item_id, p]),
  );
  const defaultPriceByItemId = new Map(defaultPrices.map(p => [p.item_id, p]));

  const priceList: PriceListEntry[] = offers.map(offer => {
    const item = itemMap.get(offer.item_id);
    const offerPrice = offerPriceByOfferId.get(offer.id);
    const defaultPrice = defaultPriceByItemId.get(offer.item_id);
    const raw = offerPrice ?? defaultPrice ?? null;
    return {
      offerId: offer.id,
      itemId: offer.item_id,
      itemName: item?.item_name ?? offer.item_id,
      itemDescription: item?.item_description ?? null,
      price: raw
        ? {
            amount: raw.amount,
            currency: raw.price_currency,
            billingRecurrence: raw.billing_recurrence,
            isInherited: !offerPrice,
          }
        : null,
    };
  });

  return { merchant, priceList };
}

export const headers = () => cacheHeaders["public-300"];

export const meta: Route.MetaFunction = ({ data: loaderData }) => {
  if (!loaderData) return [{ title: "Not Found" }];
  const name = String(loaderData.merchant.merchant_name ?? "Merchant");
  return [
    { title: `${name} Price List | Raytio Trust Marketplace` },
    {
      name: "description",
      content: `Public price list for ${name} on Raytio Trust Marketplace.`,
    },
  ];
};

export default function MerchantPriceListRoute({
  loaderData,
}: Route.ComponentProps) {
  const { merchant, priceList } = loaderData;
  const { t } = useTranslation();
  const merchantName = String(merchant.merchant_name ?? "");
  const merchantId = String(merchant.id ?? "");

  return (
    <section className="py-12">
      <Container>
        <nav className="mb-6 text-sm text-text-light">
          <Link to="/merchants" className="hover:text-accent">
            {t("PriceListPage.breadcrumb-merchants")}
          </Link>
          <span className="mx-2">/</span>
          <Link to={`/merchants/${merchantId}`} className="hover:text-accent">
            {merchantName}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-text-full">{t("PriceListPage.title")}</span>
        </nav>

        <h1 className="font-heading text-3xl font-bold text-text-full">
          {t("PriceListPage.heading", { merchant: merchantName })}
        </h1>
        <p className="mt-2 text-text-medium">{t("PriceListPage.subtitle")}</p>

        {priceList.length === 0 ? (
          <p className="mt-10 text-text-light">
            {t("PriceListPage.no-offers")}
          </p>
        ) : (
          <div className="mt-8 overflow-hidden rounded-2xl border border-border-shell/60 dark:border-transparent bg-surface-blank shadow-simple">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-shell bg-surface-elevated">
                  <th className="px-6 py-4 text-left font-heading font-semibold text-text-full">
                    {t("PriceListPage.col-item")}
                  </th>
                  <th className="px-6 py-4 text-right font-heading font-semibold text-text-full">
                    {t("PriceListPage.col-price")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-shell">
                {priceList.map(entry => (
                  <tr key={entry.offerId}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-text-full">
                        {entry.itemName}
                      </p>
                      {entry.itemDescription && (
                        <p className="mt-0.5 text-xs text-text-light">
                          {entry.itemDescription}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {entry.price ? (
                        <span className="font-medium text-text-full">
                          {new Intl.NumberFormat(undefined, {
                            style: "currency",
                            currency: entry.price.currency,
                          }).format(entry.price.amount)}
                          {entry.price.isInherited && (
                            <span className="ml-1 text-xs font-normal text-text-light">
                              {t("PriceListPage.inherited")}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-text-light">
                          {t("PriceListPage.no-price")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Container>
    </section>
  );
}
